import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';
import moment from 'moment';
import { createAuth } from '../Helpers';

export const reducerName = 'plannedItemState';

export const getAllPlans = createAsyncThunk(
    'plannedItemState/getAllPlans',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState().plannedItemState;
            const startTime = moment(state.startTime).format('YYYY-MM-DD');
            const endTime = moment(state.endTime).format('YYYY-MM-DD');
            const response = await axios({
                method: 'POST',
                url: `${config.server}/api/planneditem/search`,
                withCredentials: true,
                auth: createAuth(getState),
                data: { startTime, endTime },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const getAllColors = createAsyncThunk(
    'plannedItemState/getAllColors',
    async (_, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/colorcode`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const createPlannedItem = createAsyncThunk(
    'plannedItemState/createPlannedItem',
    async ({ personId, colorCodeId, startTime, endTime }, { getState, rejectWithValue }) => {
        const start = moment(startTime).format('YYYY-MM-DDTHH:mm:ss');
        const end = moment(endTime).format('YYYY-MM-DDTHH:mm:ss');
        try {
            const response = await axios({
                method: 'POST',
                url: `${config.server}/api/planneditem`,
                withCredentials: true,
                auth: createAuth(getState),
                data: { personId, colorCodeId, startTime: start, endTime: end },
            });
            return response.data;
        } catch (error) {
            if (error.response?.data === 'double_booking') {
                return rejectWithValue({ doubleBook: true });
            }
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const deletePlannedItem = createAsyncThunk(
    'plannedItemState/deletePlannedItem',
    async (id, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'DELETE',
                url: `${config.server}/api/planneditem/${id}`,
                withCredentials: true,
                auth: createAuth(getState),
                data: { id },
            });
            return response.data ;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

const initialState = {
    plans: [],
    days: [],
    colorCodes: [],
    startTime: moment().startOf('day').valueOf(),
    endTime: moment().startOf('day').valueOf(),
    doubleBook: false,
    loading: {
        createPlannedItem: false,
        getAllColors: false,
        getAllPlans: false,
    },
    plan: {
        personId: '',
        startTime: moment().set('hour', 7).set('minutes', 0).valueOf(),
        endTime: moment().set('hour', 15).set('minutes', 0).valueOf(),
        startValid: true,
        endValid: true,
        colorCode: null,
    },
    showPlanForm: false,
};

const plannedItemSlice = createSlice({
    name: reducerName,
    initialState,
    reducers: {
        setPersonId: (state, action) => {
            state.plan.personId = action.payload;
        },
        setInterval: (state, action) => {
            state.startTime = action.payload.start;
            state.endTime = action.payload.end;
        },
        setColorCode: (state, action) => {
            state.plan.colorCode = action.payload;
        },
        setStartTime: (state, action) => {
            const timestamp = moment(action.payload).valueOf();
            state.plan.startTime = timestamp;
            state.plan.startValid = moment(timestamp).isValid();
        },
        setEndTime: (state, action) => {
            const timestamp = moment(action.payload).valueOf();
            state.plan.endTime = timestamp;
            state.plan.endValid = moment(timestamp).isValid();
        },
        showPlanForm: (state, action) => {
            state.showPlanForm = action.payload;
            state.doubleBook = false;
        },
        showDeleteBox: (state, action) => {
            const { id, show } = action.payload;
            if (show) {
                state.plans.forEach((plan) => {
                    plan.showDelete = plan.id === id ? true : false;
                });
            } else {
                const plan = state.plans.find((plan) => plan.id === id);
                if (plan) {
                    plan.showDelete = false;
                }
            }
        },
        closeAllDeleteBoxes: (state) => {
            state.plans.forEach((plan) => {
                plan.showDelete = false;
            });
        },
    },
    extraReducers: (builder) => {
        // getAllPlans
        builder
            .addCase(getAllPlans.pending, (state) => {
                state.loading.getAllPlans = true;
                state.error = null;
            })
            .addCase(getAllPlans.fulfilled, (state, action) => {
                state.loading.getAllPlans = false;
                state.plans = action.payload.map(plan => ({ ...plan, showDelete: false }));
            })
            .addCase(getAllPlans.rejected, (state, action) => {
                state.loading.getAllPlans = false;
                state.error = action.payload || 'An error occurred';
            });

        // getAllColors
        builder
            .addCase(getAllColors.pending, (state) => {
                state.loading.getAllColors = true;
                state.error = null;
            })
            .addCase(getAllColors.fulfilled, (state, action) => {
                state.loading.getAllColors = false;
                state.colorCodes = action.payload;
            })
            .addCase(getAllColors.rejected, (state, action) => {
                state.loading.getAllColors = false;
                state.error = action.payload || 'An error occurred';
            });

        // createPlannedItem
        builder
            .addCase(createPlannedItem.pending, (state) => {
                state.loading.createPlannedItem = true;
                state.error = null;
            })
            .addCase(createPlannedItem.fulfilled, (state, action) => {
                state.loading.createPlannedItem = false;
                state.plans = [...state.plans, action.payload];
                state.showPlanForm = false;

                // Reset the plan state as in PLAN_RESET
                const start = moment().set('hour', 7).set('minutes', 0).valueOf();
                const end = moment().set('hour', 15).set('minutes', 0).valueOf();
                state.plan = {
                    ...initialState.plan,
                    startTime: start,
                    endTime: end,
                    colorCode: action.payload.colorCodeId
                };
            })
            .addCase(createPlannedItem.rejected, (state, action) => {
                state.loading.createPlannedItem = false;
                if (action.payload?.doubleBook) {
                    state.doubleBook = true;
                } else {
                    state.error = action.payload || 'An error occurred';
                }
            });
        // deletePlannedItem
        builder.addCase(deletePlannedItem.fulfilled, (state, action) => {
            state.plans = state.plans.filter((plan) => plan.id !== action.payload.id);
        });
    },
});

export const {
    setPersonId,
    setInterval,
    setColorCode,
    setStartTime,
    setEndTime,
    showPlanForm,
    showDeleteBox,
    closeAllDeleteBoxes,
} = plannedItemSlice.actions;

export default plannedItemSlice.reducer;